import { useEffect, useRef, useState } from 'react';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';

import type { ZuubGet2FAMessagesResponse } from '@/@types/zuub/contracts';

interface Payer2FAMessagesProps {
  payerName: string;
  messages?: ZuubGet2FAMessagesResponse;
  isLoading?: boolean;
}

export const Payer2FAMessages = ({
  payerName,
  messages,
  isLoading,
}: Payer2FAMessagesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAllEmailMessages, setShowAllEmailMessages] = useState(false);
  const [showAllSMSMessages, setShowAllSMSMessages] = useState(false);

  const hasHadMessagesRef = useRef(false);

  const hasMessages =
    messages &&
    (messages.emailMessages.length > 0 || messages.smsMessages.length > 0);

  // Track if we've ever had messages (for skeleton display logic)
  useEffect(() => {
    if (hasMessages) {
      hasHadMessagesRef.current = true;
    }
  }, [hasMessages]);

  // If we have messages, always show them (even if loading - show stale data)
  if (hasMessages) {
    const totalMessages =
      (messages.emailMessages.length || 0) + (messages.smsMessages.length || 0);
    const firstEmail = messages.emailMessages[0];
    const firstSMS = messages.smsMessages[0];

    return (
      <div className="vai-mt-4 vai-bg-white vai-rounded-lg vai-border vai-border-border vai-overflow-hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="vai-w-full vai-flex vai-items-center vai-justify-between vai-p-4 hover:vai-bg-gray-50 vai-transition-colors">
            <div className="vai-flex vai-items-center vai-gap-2">
              <span className="vai-font-medium vai-text-sm vai-text-gray-900">
                <Trans>2FA Messages for {payerName}</Trans>
              </span>
              {totalMessages > 0 && (
                <span className="vai-inline-flex vai-items-center vai-justify-center vai-w-5 vai-h-5 vai-rounded-full vai-bg-blue-500 vai-text-white vai-text-xs vai-font-medium">
                  {totalMessages}
                </span>
              )}
            </div>
            <FontAwesomeIcon
              icon={isOpen ? faChevronUp : faChevronDown}
              className="vai-text-gray-500 vai-text-sm"
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="vai-px-4 vai-pb-4 vai-space-y-4">
            {/* Email Messages - Show latest or all */}
            {messages.emailMessages.length > 0 && (
              <div className="vai-space-y-3">
                {showAllEmailMessages ? (
                  <>
                    <h3 className="vai-text-sm vai-font-semibold vai-text-gray-700 vai-mb-2">
                      <Trans>Email Messages</Trans> (
                      {messages.emailMessages.length})
                    </h3>
                    {messages.emailMessages.map((email, index) => (
                      <div
                        key={index}
                        className="vai-border vai-border-gray-200 vai-rounded-md vai-p-3 vai-bg-gray-50"
                      >
                        <div className="vai-flex vai-items-center vai-justify-between vai-mb-2">
                          <span className="vai-text-xs vai-font-semibold vai-text-gray-700 vai-uppercase">
                            <Trans>Email Message</Trans> {index + 1}
                          </span>
                          {email.timestamp && (
                            <span className="vai-text-xs vai-text-gray-500">
                              {new Date(email.timestamp).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {email.code && (
                          <div className="vai-mb-2">
                            <span className="vai-text-xs vai-font-medium vai-text-gray-600">
                              <Trans>Code:</Trans>
                            </span>
                            <span className="vai-ml-2 vai-font-mono vai-text-sm vai-font-bold vai-text-blue-600">
                              {email.code}
                            </span>
                          </div>
                        )}
                        <div className="vai-text-sm vai-text-gray-800">
                          {email.text || email.html.replace(/<[^>]*>/g, '')}
                        </div>
                      </div>
                    ))}
                    {messages.emailMessages.length > 1 && (
                      <button
                        onClick={() => setShowAllEmailMessages(false)}
                        className="vai-text-sm vai-text-gray-600 hover:vai-text-gray-800 vai-font-medium vai-cursor-pointer"
                      >
                        <Trans>Show fewer email messages</Trans>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="vai-border vai-border-gray-200 vai-rounded-md vai-p-3 vai-bg-gray-50">
                      <div className="vai-flex vai-items-center vai-justify-between vai-mb-2">
                        <span className="vai-text-xs vai-font-semibold vai-text-gray-700 vai-uppercase">
                          <Trans>Latest Email Message</Trans>
                        </span>
                        {firstEmail.timestamp && (
                          <span className="vai-text-xs vai-text-gray-500">
                            {new Date(firstEmail.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {firstEmail.code && (
                        <div className="vai-mb-2">
                          <span className="vai-text-xs vai-font-medium vai-text-gray-600">
                            <Trans>Code:</Trans>
                          </span>
                          <span className="vai-ml-2 vai-font-mono vai-text-sm vai-font-bold vai-text-blue-600">
                            {firstEmail.code}
                          </span>
                        </div>
                      )}
                      <div className="vai-text-sm vai-text-gray-800">
                        {firstEmail.text ||
                          firstEmail.html.replace(/<[^>]*>/g, '')}
                      </div>
                    </div>
                    {messages.emailMessages.length > 1 && (
                      <button
                        onClick={() => setShowAllEmailMessages(true)}
                        className="vai-text-sm vai-text-blue-600 hover:vai-text-blue-800 vai-font-medium vai-cursor-pointer"
                      >
                        <Trans>View</Trans> {messages.emailMessages.length - 1}{' '}
                        <Trans>more email</Trans>
                        {messages.emailMessages.length - 1 !== 1 ? 's' : ''}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* SMS Messages - Show latest or all */}
            {messages.smsMessages.length > 0 && (
              <div className="vai-space-y-3">
                {showAllSMSMessages ? (
                  <>
                    <h3 className="vai-text-sm vai-font-semibold vai-text-gray-700 vai-mb-2">
                      <Trans>SMS Messages</Trans> ({messages.smsMessages.length}
                      )
                    </h3>
                    {messages.smsMessages.map((sms, index) => (
                      <div
                        key={index}
                        className="vai-border vai-border-gray-200 vai-rounded-md vai-p-3 vai-bg-gray-50"
                      >
                        <div className="vai-flex vai-items-center vai-justify-between vai-mb-2">
                          <span className="vai-text-xs vai-font-semibold vai-text-gray-700 vai-uppercase">
                            <Trans>SMS Message</Trans> {index + 1}
                          </span>
                          {sms.timestamp && (
                            <span className="vai-text-xs vai-text-gray-500">
                              {new Date(sms.timestamp).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {sms.code && (
                          <div className="vai-mb-2">
                            <span className="vai-text-xs vai-font-medium vai-text-gray-600">
                              <Trans>Code:</Trans>
                            </span>
                            <span className="vai-ml-2 vai-font-mono vai-text-sm vai-font-bold vai-text-blue-600">
                              {sms.code}
                            </span>
                          </div>
                        )}
                        <div className="vai-text-sm vai-text-gray-800">
                          {sms.message}
                        </div>
                      </div>
                    ))}
                    {messages.smsMessages.length > 1 && (
                      <button
                        onClick={() => setShowAllSMSMessages(false)}
                        className="vai-text-sm vai-text-gray-600 hover:vai-text-gray-800 vai-font-medium vai-cursor-pointer"
                      >
                        <Trans>Show fewer SMS messages</Trans>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="vai-border vai-border-gray-200 vai-rounded-md vai-p-3 vai-bg-gray-50">
                      <div className="vai-flex vai-items-center vai-justify-between vai-mb-2">
                        <span className="vai-text-xs vai-font-semibold vai-text-gray-700 vai-uppercase">
                          <Trans>Latest SMS Message</Trans>
                        </span>
                        {firstSMS.timestamp && (
                          <span className="vai-text-xs vai-text-gray-500">
                            {new Date(firstSMS.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {firstSMS.code && (
                        <div className="vai-mb-2">
                          <span className="vai-text-xs vai-font-medium vai-text-gray-600">
                            <Trans>Code:</Trans>
                          </span>
                          <span className="vai-ml-2 vai-font-mono vai-text-sm vai-font-bold vai-text-blue-600">
                            {firstSMS.code}
                          </span>
                        </div>
                      )}
                      <div className="vai-text-sm vai-text-gray-800">
                        {firstSMS.message}
                      </div>
                    </div>
                    {messages.smsMessages.length > 1 && (
                      <button
                        onClick={() => setShowAllSMSMessages(true)}
                        className="vai-text-sm vai-text-blue-600 hover:vai-text-blue-800 vai-font-medium vai-cursor-pointer"
                      >
                        <Trans>View</Trans> {messages.smsMessages.length - 1}{' '}
                        <Trans>more SMS message</Trans>
                        {messages.smsMessages.length - 1 !== 1 ? 's' : ''}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  // No messages currently - only show skeleton if we've had messages before (reloading)
  if (isLoading && hasHadMessagesRef.current) {
    return (
      <div className="vai-mt-4 vai-bg-white vai-rounded-lg vai-border vai-border-border vai-p-4">
        <div className="vai-animate-pulse">
          <div className="vai-h-4 vai-bg-gray-200 vai-rounded vai-w-1/4 vai-mb-2"></div>
          <div className="vai-h-4 vai-bg-gray-200 vai-rounded vai-w-1/2"></div>
        </div>
      </div>
    );
  }

  // No messages and not reloading - don't render anything
  return null;
};
